import {
    useGetRelatedScenarioResultsLazyQuery,
    useGetRemainingScenarioResultsLazyQuery,
    useGetScenarioResultsByProjectIdLazyQuery
} from "generated/graphql";
import { TrackedProject } from "in_queue/types/projectType";
import {
    convertHasuraScenarioDataToScenarioResults,
    convertHasuraScenarioDataToScenarioResultsWithExistingData,
    ScenarioResults
} from "in_queue/types/scenarioResultType";
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import { Loading } from "types/loadingType";
import { useRegionConfig } from "./ClusterContext";
import { useSubscriptionForCluster } from "./ProjectDataContext";

interface CompareScenarioResultsContextValue {
    resultsA: Loading<ScenarioResults>;
    resultsB: Loading<ScenarioResults>;
    /**
     * `true` while the second fetch (remaining / related project results)
     * is still in flight for either scenario, on top of the initial
     * current-project fetch. Cross-project numbers are incomplete until it
     * flips `false`.
     *
     * Resets to `true` whenever either scenario id changes.
     */
    isLoadingRemaining: boolean;
}

const CompareScenarioResultsContext =
    createContext<CompareScenarioResultsContextValue>({
        resultsA: "loading",
        resultsB: "loading",
        isLoadingRemaining: true
    });

export const CompareScenarioResultsProvider: React.FC<
    PropsWithChildren<{
        project: Pick<TrackedProject, "projectId">;
        scenarioIdA: string;
        scenarioIdB: string;
    }>
> = ({ project: { projectId }, scenarioIdA, scenarioIdB, children }) => {
    const project = useMemo(() => ({ projectId }), [projectId]);
    const regionConfig = useRegionConfig();
    const subscription = useSubscriptionForCluster();
    const subscriptionReady = subscription !== "loading";
    const hasUnlimitedProjects =
        subscription !== "loading" && subscription?.projectLimit === null;

    const [resultsA, setResultsA] =
        useState<Loading<ScenarioResults>>("loading");
    const [resultsB, setResultsB] =
        useState<Loading<ScenarioResults>>("loading");
    const [isLoadingRemaining, setIsLoadingRemaining] = useState(true);

    const [getInitialA] = useGetScenarioResultsByProjectIdLazyQuery();
    const [getInitialB] = useGetScenarioResultsByProjectIdLazyQuery();
    const [getRemainingA] = useGetRemainingScenarioResultsLazyQuery();
    const [getRemainingB] = useGetRemainingScenarioResultsLazyQuery();
    const [getRelatedA] = useGetRelatedScenarioResultsLazyQuery();
    const [getRelatedB] = useGetRelatedScenarioResultsLazyQuery();

    useEffect(() => {
        if (!subscriptionReady) {
            return;
        }

        let cancelled = false;
        setIsLoadingRemaining(true);

        const fetchScenario = async (
            scenarioId: string,
            getInitial: typeof getInitialA,
            getRemaining: typeof getRemainingA,
            getRelated: typeof getRelatedA,
            setResults: typeof setResultsA
        ) => {
            const { data: initialData } = await getInitial({
                variables: { scenarioId, projectId: project.projectId }
            });
            if (cancelled || !initialData) {
                return;
            }
            let results = convertHasuraScenarioDataToScenarioResults(
                initialData,
                project,
                regionConfig
            );

            // Publish the current project's rows immediately so the page
            // renders while the cross-project fetch is still in flight. Both
            // detail tabs open scoped to this project, and scenario_projects
            // arrives whole, so this first batch fills the default view.
            setResults(results);

            if (hasUnlimitedProjects) {
                const { data: remainingData } = await getRemaining({
                    variables: { scenarioId, projectId: project.projectId }
                });
                if (cancelled || !remainingData) {
                    return;
                }
                results =
                    convertHasuraScenarioDataToScenarioResultsWithExistingData({
                        hasuraResult: remainingData,
                        existingScenarioResults: results,
                        currentProject: project,
                        regionConfig
                    });
                setResults(results);
            } else {
                const networkUpgradeNames = results.allocatedUpgrades.map(
                    (u) => u.networkUpgradeName
                );
                const monitoredFacilityNames = results.constraints.map(
                    (c) => c.monitoredFacilityName
                );
                const { data: relatedData } = await getRelated({
                    variables: {
                        scenarioId,
                        projectId: project.projectId,
                        networkUpgradeNames,
                        monitoredFacilityNames
                    }
                });
                if (cancelled || !relatedData) {
                    return;
                }
                results =
                    convertHasuraScenarioDataToScenarioResultsWithExistingData({
                        hasuraResult: relatedData,
                        existingScenarioResults: results,
                        currentProject: project,
                        regionConfig
                    });
                setResults(results);
            }
        };

        Promise.all([
            fetchScenario(
                scenarioIdA,
                getInitialA,
                getRemainingA,
                getRelatedA,
                setResultsA
            ),
            fetchScenario(
                scenarioIdB,
                getInitialB,
                getRemainingB,
                getRelatedB,
                setResultsB
            )
        ]).finally(() => {
            if (!cancelled) {
                setIsLoadingRemaining(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        scenarioIdA,
        scenarioIdB,
        project,
        regionConfig,
        subscriptionReady,
        hasUnlimitedProjects
    ]);

    return (
        <CompareScenarioResultsContext.Provider
            value={{ resultsA, resultsB, isLoadingRemaining }}
        >
            {children}
        </CompareScenarioResultsContext.Provider>
    );
};

export const useCompareScenarioResults =
    (): CompareScenarioResultsContextValue =>
        useContext(CompareScenarioResultsContext);
